import React from "react";
import GooglePlacesAutocomplete from "./GooglePlacesAutocomplete";
import { getPlaceDetails } from "../../api/location";

/**
 * Google address picker used by the booking form's pickup and drop sections.
 * Resolves the picked suggestion to its full address and coordinates and calls
 * onSelect(place), or onSelect(null) when the field is cleared. If the place
 * lookup fails, `place.detailsError` says why and lat/lng are null.
 */
export default function DummyPlacesAutocomplete({
  value,
  onChange,
  onSelect,
  placeholder = "Search Google address",
  apiKey,
  city = null,
  ...props
}) {
  const handleSelect = async (result) => {
    if (!result) {
      onSelect(null);
      return;
    }

    const fallback = {
      formattedAddress: result.description || "",
      name: result.structured_formatting?.main_text || "",
      lat: null,
      lng: null,
      city: "",
      state: "",
      country: "",
      postalCode: "",
      placeId: result.place_id || "",
      types: result.types || [],
    };

    if (!result.place_id) {
      onSelect({ ...fallback, detailsError: "This suggestion has no Google place ID, so its exact location is unknown." });
      return;
    }

    try {
      const details = await getPlaceDetails(result.place_id);
      const location = details?.geometry?.location;
      onSelect({
        ...fallback,
        formattedAddress: details.fullAddress || details.formattedAddress || fallback.formattedAddress,
        name: details.name || fallback.name,
        lat: location?.lat ?? null,
        lng: location?.lng ?? null,
        city: details.cityName || "",
        state: details.stateName || "",
        country: details.countryName || "",
        postalCode: details.postalCode || "",
        types: details.types?.length ? details.types : fallback.types,
        ...(location ? {} : { detailsError: "Google returned no coordinates for this place." }),
      });
    } catch (error) {
      onSelect({
        ...fallback,
        detailsError: error?.response?.data?.error || "Could not load this place's exact location from Google.",
      });
    }
  };

  return (
    <GooglePlacesAutocomplete
      value={value}
      onChange={onChange}
      onSelect={handleSelect}
      placeholder={placeholder}
      minLength={4}
      debounceDelay={300}
      showClearButton={true}
      className="w-full"
      dropdownClassName="rounded-lg"
      city={city}
      {...props}
    />
  );
}
